import React, { useEffect, useState, useSyncExternalStore } from "react";
import ResultsWorkspaceOverlays from "./ResultsWorkspaceOverlays.jsx";
import ResultsWorkspacePanels from "./ResultsWorkspacePanels.jsx";
import { WorkspaceShell } from "./ResultsPrimitives.jsx";
import useErrorsCenterController from "../hooks/useErrorsCenterController.js";
import usePullForwardController from "../features/pullForward/usePullForwardController.js";
import useResultsWorkspaceController from "../hooks/useResultsWorkspaceController.js";
import useResultsWorkspaceState from "../hooks/useResultsWorkspaceState.js";
import { buildFleetCards } from "../utils/resultsFleetData.js";
import {
  consumeResultsShellNavigation,
  getResultsShellNavigationSnapshot,
  normalizeDetailViewName,
  subscribeResultsShellNavigation,
} from "../utils/resultsDetailViewNavigation.js";

export default function ResultsWorkspace({
  detailViewState,
  initialResultsData = null,
}) {
  const errorsCenterController = useErrorsCenterController();
  const { openErrorsPanel, openRequest } = errorsCenterController;
  const [highlightedRouteSegmentIndex, setHighlightedRouteSegmentIndex] = useState(null);
  const shellNavigationRequest = useSyncExternalStore(
    subscribeResultsShellNavigation,
    getResultsShellNavigationSnapshot,
    getResultsShellNavigationSnapshot,
  );
  const workspaceState = useResultsWorkspaceState({
    buildFleetCards,
    detailViewState,
    initialResultsData,
  });

  const {
    closeReassignmentPopover,
    handleClientSelection,
    handleMapRenderMetaChange,
    mapRenderMeta,
    reassignmentPopover,
    renderCurrentSelection,
    selectionRequest,
    setMapController,
    stateRef,
    workspaceStore,
    workspaceElements,
    workspaceUi,
    syncWorkspaceUi,
  } = workspaceState;
  const { planningMutation, workspaceInteractions } = useResultsWorkspaceController({
    detailViewState,
    initialResultsData,
    openErrorsPanel,
    workspaceState,
  });
  const pullForwardController = usePullForwardController({
    initialResultsData,
    renderCurrentSelection,
    syncWorkspaceUi,
    workspaceStore,
  });
  const sharedWorkspaceInteractions = React.useMemo(() => ({
    ...workspaceInteractions,
    chrome: {
      ...(workspaceInteractions.chrome || {}),
      pullForward: pullForwardController,
    },
    pullForward: pullForwardController,
  }), [pullForwardController, workspaceInteractions]);

  useEffect(() => {
    const requestedView = normalizeDetailViewName(shellNavigationRequest.detail?.viewName);
    if ((requestedView !== "errors" && requestedView !== "warnings") || shellNavigationRequest.detail?.errorsOpenAll !== true) {
      return;
    }

    closeReassignmentPopover({ rerender: false });
    openErrorsPanel({
      errorId: typeof shellNavigationRequest.detail?.errorId === "string" ? shellNavigationRequest.detail.errorId : "",
      filter: typeof shellNavigationRequest.detail?.filter === "string" ? shellNavigationRequest.detail.filter : "",
      openAll: true,
      scroll: shellNavigationRequest.detail?.scroll !== false,
      source: typeof shellNavigationRequest.detail?.source === "string" && shellNavigationRequest.detail.source
        ? shellNavigationRequest.detail.source
        : "shell-nav",
      viewName: requestedView,
    });
    consumeResultsShellNavigation(shellNavigationRequest.token);
  }, [closeReassignmentPopover, openErrorsPanel, shellNavigationRequest]);

  // Clics de la marge de droite : changer de journée ou sélectionner un camion dans le Gantt.
  useEffect(() => {
    const dispatchDate = typeof shellNavigationRequest.detail?.dispatchDate === "string" ? shellNavigationRequest.detail.dispatchDate : "";
    const truckId = typeof shellNavigationRequest.detail?.truckId === "string" ? shellNavigationRequest.detail.truckId : "";
    if (!dispatchDate && !truckId) {
      return;
    }

    closeReassignmentPopover({ rerender: false });
    if (dispatchDate) {
      sharedWorkspaceInteractions.chrome?.onSelectDispatchDay?.(dispatchDate);
    }
    if (truckId) {
      sharedWorkspaceInteractions.assignmentCard?.onSelectAssignment?.(truckId);
    }
    consumeResultsShellNavigation(shellNavigationRequest.token);
  }, [closeReassignmentPopover, sharedWorkspaceInteractions, shellNavigationRequest]);

  return (
    <WorkspaceShell>
      <ResultsWorkspacePanels
        detailViewState={detailViewState}
        highlightedRouteSegmentIndex={highlightedRouteSegmentIndex}
        initialResultsData={initialResultsData}
        interactions={sharedWorkspaceInteractions}
        mapRenderMeta={mapRenderMeta}
        onClientSelect={handleClientSelection}
        onMapControllerReady={setMapController}
        onMapRenderMetaChange={handleMapRenderMetaChange}
        onRouteHighlightChange={setHighlightedRouteSegmentIndex}
        onToggleView={(viewName) => {
          closeReassignmentPopover({ rerender: false });
          detailViewState.toggleView(viewName, {
            historyMode: "replace",
            scroll: false,
            syncLocation: true,
          });
        }}
        selectionRequest={selectionRequest}
        stateRef={stateRef}
        workspaceStore={workspaceStore}
        workspaceElements={workspaceElements}
        workspaceUi={workspaceUi}
        errorsCenterOpenRequest={openRequest}
      />

      <ResultsWorkspaceOverlays
        closeReassignmentPopover={closeReassignmentPopover}
        planningMutation={planningMutation}
        pullForwardController={pullForwardController}
        reassignmentPopover={reassignmentPopover}
      />
    </WorkspaceShell>
  );
}
